import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { AdminViews } from 'src/routes/routesConfig';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from 'src/shared/components/ui/sheet';
import { Input } from 'src/shared/components/ui/input';
import { Button } from 'src/shared/components/ui/button';
import { useUpdateCustomerMutation } from 'src/app/admin/state/api/adminGraphSlice';
import {
    isConflictError,
    isFetchError,
    useRenameOrganisationMutation,
} from 'src/app/admin/views/risk-portfolio/state/api/organisationEngineSlice';
import useCanAdministerOrganisations from 'src/app/admin/hooks/useCanAdministerOrganisations';
import DeleteOrganisationDialog from 'src/app/admin/components/delete-organisation-dialog/DeleteOrganisationDialog';

type ManageOrganisationSheetProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    customerId: string;
    customerName: string;
};

const RENAME_FAILED = 'Something went wrong updating the organisation. Please try again.';

const ARCHIVED_IN_NATURE_RISK = 'This organisation is archived in Nature Risk. Restore it before renaming it.';

const RENAMED_IN_NATURE_RISK_ONLY = 'Renamed in Nature Risk, but not yet in Nature Performance. Save again to finish.';

/**
 * Everything that can be done to one organisation: update what is editable — today its name alone —
 * or remove it altogether. Both live here because both are reached from the same place, and an
 * operator deciding between them should not have to go looking for the other.
 */
const ManageOrganisationSheet = ({ open, onOpenChange, customerId, customerName }: ManageOrganisationSheetProps) => {
    const [renameOrganisation, { isLoading: isRenamingInNatureRisk }] = useRenameOrganisationMutation();
    const [updateCustomer, { isLoading: isRenamingInNaturePerformance }] = useUpdateCustomerMutation();
    const isLoading = isRenamingInNatureRisk || isRenamingInNaturePerformance;
    const [name, setName] = useState(customerName);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const navigate = useNavigate();
    const { canDelete } = useCanAdministerOrganisations();

    /**
     * The organisation this sheet was opened for no longer exists, so wherever we were is now a page
     * about nothing — on the Nature Performance page, literally "could not be found". Leave for the
     * list, which is the one place that still has something to show.
     */
    const handleDeleted = () => {
        onOpenChange(false);
        navigate(navRoutesConfig[AdminViews.ORGANISATIONS] as string);
    };

    useEffect(() => {
        setName(customerName);
    }, [customerName]);

    const trimmedName = name.trim();
    // A change that changes nothing is not worth sending to a super-admin-only mutation.
    const canSubmit = trimmedName !== '' && trimmedName !== customerName && !isLoading;

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            setName(customerName);
            setErrorMessage(null);
        }
        onOpenChange(nextOpen);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSubmit) {
            return;
        }
        setErrorMessage(null);
        // Engine first, so a Go failure leaves Save enabled to finish the rename (ADR 006).
        const natureRisk = await renameOrganisation({ bcId: customerId, name: trimmedName })
            .unwrap()
            .catch((error: unknown) => {
                setErrorMessage(isFetchError(error) && isConflictError(error) ? ARCHIVED_IN_NATURE_RISK : RENAME_FAILED);
                return null;
            });
        if (!natureRisk) {
            return;
        }
        try {
            await updateCustomer({ customerId, customerName: trimmedName }).unwrap();
            onOpenChange(false);
        } catch {
            setErrorMessage(natureRisk === 'renamed' ? RENAMED_IN_NATURE_RISK_ONLY : RENAME_FAILED);
        }
    };

    return (
        <Sheet open={open} onOpenChange={handleOpenChange}>
            <SheetContent>
                <SheetHeader>
                    <SheetTitle>Manage organisation</SheetTitle>
                    <SheetDescription>
                        Update the details of {customerId}, or delete it entirely. The Customer ID cannot be changed.
                    </SheetDescription>
                </SheetHeader>

                <form onSubmit={handleSubmit} className='flex flex-col gap-4 mt-6' data-testid='manage-organisation-form'>
                    <div className='flex flex-col gap-1'>
                        <label htmlFor='organisation-name' className='text-sm font-medium'>
                            Organisation name
                        </label>
                        <Input id='organisation-name' value={name} onChange={event => setName(event.target.value)} />
                    </div>

                    {errorMessage && (
                        <p role='alert' className='text-sm text-red-100'>
                            {errorMessage}
                        </p>
                    )}

                    <SheetFooter className='mt-2'>
                        <Button type='button' variant='outline' onClick={() => handleOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type='submit' disabled={!canSubmit} className='flex-grow'>
                            Save changes
                        </Button>
                    </SheetFooter>
                </form>

                {/* Kept outside the form and below its actions: a different kind of act, and irreversible. */}
                {canDelete && (
                    <div className='mt-8 pt-6 border-t border-grey-20 flex flex-col gap-2'>
                        <span className='text-sm font-medium'>Delete organisation</span>
                        <p className='text-sm text-grey-70'>
                            Removes {customerId} and everything beneath it. You will be shown what this would take with it before anything
                            happens.
                        </p>
                        <Button
                            type='button'
                            variant='outline'
                            className='self-start mt-1 text-red-100 border-red-100 hover:bg-red-10'
                            onClick={() => setIsDeleteOpen(true)}
                            data-testid='manage-organisation-delete'
                        >
                            <Trash2 size={16} />
                            Delete organisation
                        </Button>
                    </div>
                )}

                <DeleteOrganisationDialog
                    open={isDeleteOpen}
                    onOpenChange={setIsDeleteOpen}
                    customerId={customerId}
                    customerName={customerName}
                    onDeleted={handleDeleted}
                />
            </SheetContent>
        </Sheet>
    );
};

export default ManageOrganisationSheet;
